/**
 * Cart Terms & Conditions Checkbox - Custom Boujee Addition
 *
 * Blocks checkout when terms checkbox is unchecked
 * Changes button text to "Please agree to terms" when unchecked
 * Glows with #bb7756 gradient when user tries to checkout without agreeing
 * Remembers user's checkbox state throughout the session using sessionStorage
 * Works on both cart drawer and cart page
 */

class CartTermsCheckbox {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }

    // Observe the cart drawer for DOM changes.
    // Multiple code paths replace cart drawer innerHTML (CartItems.onCartUpdate,
    // CartDrawer.onCartRefresh, product-card-subscription-v2 via cart:refresh).
    // Event-based re-init doesn't work reliably because cart:refresh fires BEFORE
    // the async onCartRefresh replaces the DOM. A MutationObserver fires AFTER the
    // actual DOM change, regardless of which code path triggered it.
    this.observeCartContainers();
  }

  observeCartContainers() {
    let debounceTimer = null;
    const runSetup = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => this.setup(), 50);
    };

    // Observe cart drawer (innerHTML replaced by CartItems.onCartUpdate and CartDrawer.onCartRefresh)
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      new MutationObserver(runSetup).observe(cartDrawer, { childList: true, subtree: true });
    }

    // Observe cart page container (innerHTML replaced by CartItems.onCartUpdate on /cart page)
    const mainCart = document.querySelector('[id^="MainCart-"]');
    if (mainCart) {
      new MutationObserver(runSetup).observe(mainCart, { childList: true, subtree: true });
    }
  }

  setup() {
    // Find all checkout buttons
    this.checkoutButtons = document.querySelectorAll('[data-checkout-button]');

    // Find all terms checkboxes
    this.termsCheckboxes = document.querySelectorAll('[data-cart-terms-checkbox]');

    this.termsTextWrappers = document.querySelectorAll('[data-cart-terms-text]');

    if (!this.termsCheckboxes.length) {
      return;
    }

    // Restore checkbox state from session storage
    this.restoreCheckboxState();

    // Add event listeners to all checkboxes (skip if already initialized)
    this.termsCheckboxes.forEach(checkbox => {
      if (checkbox.dataset.termsInit) return;
      checkbox.dataset.termsInit = 'true';
      checkbox.addEventListener('change', () => {
        this.saveCheckboxState(checkbox.checked);
        this.updateCheckoutButtons();
      });
    });

    // Add click handlers to checkout buttons (skip if already initialized)
    this.checkoutButtons.forEach(button => {
      if (button.dataset.termsInit) return;
      button.dataset.termsInit = 'true';
      button.addEventListener('click', (e) => this.handleCheckoutClick(e));
    });

    // Initial state update
    this.updateCheckoutButtons();
    this.setupTermsDrawer();
  }

  findTermsCheckbox(button) {
    // For drawer: checkbox is now sibling of cart-items in drawer body
    // For page: checkbox is in footer area
    const isInDrawer = button.closest('.cart-drawer');
    const context = isInDrawer ? 'drawer' : 'page';
    return document.querySelector(`#ih-cart-terms-${context}`);
  }

  findTermsWrapper(button) {
    // Find the terms wrapper relative to the button
    const isInDrawer = button.closest('.cart-drawer');
    if (isInDrawer) {
      // In drawer, checkbox is in the drawer body
      return button.closest('.cart-drawer').querySelector('[data-cart-terms-wrapper]');
    } else {
      // In page, checkbox is near the button
      const footer = button.closest('.cart__footer-sticky');
      return footer ? footer.querySelector('[data-cart-terms-wrapper]') : null;
    }
  }

  handleCheckoutClick(e) {
    const button = e.currentTarget;
    const checkbox = this.findTermsCheckbox(button);

    if (!checkbox || !checkbox.checked) {
      e.preventDefault();
      e.stopPropagation();

      // Glow flash the checkbox wrapper to draw attention
      const termsWrapper = this.findTermsWrapper(button);
      if (termsWrapper) {
        termsWrapper.classList.add('ih-cart-terms--glow');
        setTimeout(() => {
          termsWrapper.classList.remove('ih-cart-terms--glow');
          // Focus the checkbox after glow animation completes for keyboard users
          if (checkbox) {
            checkbox.focus();
          }
        }, 1000); // 1 second to match animation duration
      }

      return false;
    }
  }

  saveCheckboxState(isChecked) {
    try {
      sessionStorage.setItem('cart_terms_agreed', isChecked ? 'true' : 'false');
    } catch (e) {
      // SessionStorage not available (private browsing, etc.)
      console.warn('Unable to save terms checkbox state:', e);
    }
  }

  restoreCheckboxState() {
    try {
      const savedState = sessionStorage.getItem('cart_terms_agreed');

      if (savedState !== null) {
        const isChecked = savedState === 'true';

        // Apply saved state to all checkboxes
        this.termsCheckboxes.forEach(checkbox => {
          checkbox.checked = isChecked;
        });
      }
    } catch (e) {
      // SessionStorage not available (private browsing, etc.)
      console.warn('Unable to restore terms checkbox state:', e);
    }
  }

  updateCheckoutButtons() {
    this.checkoutButtons.forEach(button => {
      const checkbox = this.findTermsCheckbox(button);

      if (checkbox) {
        // Get button text element
        const buttonText = button.querySelector('.btn__text');

        // Store original text on first run
        if (!button.hasAttribute('data-original-text') && buttonText) {
          button.setAttribute('data-original-text', buttonText.textContent.trim());
        }

        // Update button state when checkbox is checked/unchecked
        if (checkbox.checked) {
          button.removeAttribute('data-terms-unchecked');
          button.removeAttribute('aria-disabled');
          button.style.opacity = '';
          button.style.cursor = '';

          // Restore original button text
          if (buttonText && button.hasAttribute('data-original-text')) {
            buttonText.textContent = button.getAttribute('data-original-text');
          }
        } else {
          button.setAttribute('data-terms-unchecked', 'true');
          button.setAttribute('aria-disabled', 'true');
          button.style.opacity = '0.5';
          button.style.cursor = 'not-allowed';

          // Change button text to indicate terms must be agreed to
          if (buttonText) {
            buttonText.textContent = 'Please agree to terms';
          }
        }
      }
    });
  }

  setupReadMoreLinks() {
    this.termsTextWrappers = document.querySelectorAll('[data-cart-terms-text]');

    if (!this.termsTextWrappers || !this.termsTextWrappers.length) {
      return;
    }

    this.termsTextWrappers.forEach(wrapper => {
      if (wrapper.dataset.termsInit) return;
      wrapper.dataset.termsInit = 'true';
      const readMore = wrapper.querySelector('[data-cart-terms-read-more]');

      if (!readMore) {
        return;
      }

      // Click handler to expand/collapse
      readMore.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const isExpanded = wrapper.classList.toggle('ih-cart-terms__text--expanded');
        readMore.textContent = isExpanded ? 'Read less' : 'Read more';
        readMore.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      });
    });
  }

  setupTermsDrawer() {
    const termsLinks = document.querySelectorAll('[data-cart-terms-link]');

    if (!termsLinks || !termsLinks.length) {
      return;
    }

    termsLinks.forEach(link => {
      if (link.dataset.termsInit) return;
      link.dataset.termsInit = 'true';
      link.addEventListener('click', (event) => {
        // Prevent default navigation and open drawer instead
        event.preventDefault();

        // Get the drawer ID from aria-controls
        const drawerId = link.getAttribute('aria-controls');
        const drawer = document.getElementById(drawerId);

        if (!drawer) return;

        // Check if content already loaded
        const drawerBody = drawer.querySelector('[data-cart-terms-drawer-body]');
        if (!drawerBody) return;

        // If content is still "Loading...", fetch it
        if (drawerBody.textContent.trim() === 'Loading...') {
          this.fetchTermsContent(drawerBody);
        }

        // Open the drawer (remove hidden attribute)
        drawer.removeAttribute('hidden');
      });
    });
  }

  fetchTermsContent(drawerBody) {
    fetch('/pages/terms-and-conditions')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch terms page');
        }
        return response.text();
      })
      .then(html => {
        // Parse the HTML response
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract main content - try common selectors
        let content = doc.querySelector('.page-content') ||
                     doc.querySelector('.rte') ||
                     doc.querySelector('main') ||
                     doc.querySelector('.main-content');

        if (content) {
          drawerBody.innerHTML = content.innerHTML;
        } else {
          drawerBody.innerHTML = '<p>Unable to load terms and conditions. Please visit the <a href="/pages/terms-and-conditions">terms page</a>.</p>';
        }
      })
      .catch(error => {
        console.error('Error fetching terms content:', error);
        drawerBody.innerHTML = '<p>Unable to load terms and conditions. Please visit the <a href="/pages/terms-and-conditions">terms page</a>.</p>';
      });
  }
}

// Initialize when DOM is ready
new CartTermsCheckbox();
